import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import { task } from 'ember-concurrency';
import SegmentsManageAudiencesDetails from 'cio/components/segments/manage-audiences/details';
import SegmentsManageAudiencesToggleState from 'cio/components/segments/manage-audiences/toggle-state';
import SegmentsManageAudiencesDelete from 'cio/components/segments/manage-audiences/delete';
import { formatAudiencesError, TYPES } from 'cio/utils/ad-audiences';

export default class AudiencesController extends Controller {
  @service analytics;
  @service alerts;
  @service apiErrors;
  @service store;

  queryParams = ['isAdding', 'audienceAction', 'audienceId'];
  isAdding = false;
  audienceId = null;
  audienceAction = null;

  @computed('model.audiences.content.@each.isNew')
  get _audiences() {
    return this.model.audiences.content.filterBy('isNew', false);
  }

  @computed('audienceId')
  get audienceToManage() {
    return this.model.audiences.content.findBy('id', this.audienceId);
  }

  _pauseAudienceTask = task(
    {
      drop: true,
    },
    async () => {
      let { audienceToManage: audience } = this;

      // Set active true if currently paused; false if currently active
      audience.set('active', Boolean(audience.paused) || Boolean(audience.confirmConsent));

      if (audience.type === TYPES.adwords && audience.confirmConsent === true) {
        audience.set('hasConsent', true);
      }
      try {
        await audience.save();
        this._stopManaging();
        this.alerts.success(
          'toggled-state.audience',
          audience.active ? 'Resumed syncing audience!' : 'Audience syncing paused',
        );
      } catch (err) {
        this.apiErrors.report(err);
        throw err;
      }
    },
  );

  _deleteAudienceTask = task(
    {
      drop: true,
    },
    async () => {
      let { audienceToManage: audience } = this;

      try {
        await audience.destroyRecord();
        this._stopManaging();
        this.alerts.success('deleted.audience', 'Audience deleted');
      } catch (err) {
        this.apiErrors.report(err);
        throw err;
      }
    },
  );

  createAudienceTask = task(async (attrs) => {
    let audience = this.store.createRecord('audience', attrs);
    audience.set('hasConsent', true);

    try {
      await audience.save();
      return audience;
    } catch (res) {
      audience.rollbackAttributes();

      let {
        errors: [error],
      } = res;

      let message;

      if (Number(error.detail.status) === 422) {
        if (attrs.type === TYPES.facebook) {
          message = error.detail.meta ? error.detail.meta.user_message : error.detail.detail;
        } else if (attrs.type === TYPES.adwords) {
          message = error.detail.meta ? error.detail.meta.message : error.detail.detail;
        }
      } else {
        message = 'Something went wrong. Please try again.';
      }

      throw new Error(formatAudiencesError(message));
    }
  });

  _stopManaging() {
    this.setProperties({
      audienceAction: null,
      audienceId: null,
    });
  }

  @computed('audienceAction')
  // eslint-disable-next-line getter-return
  get _activeComponent() {
    if (this.audienceAction === 'manage') {
      return SegmentsManageAudiencesDetails;
    }

    if (this.audienceAction === 'toggle-state') {
      return SegmentsManageAudiencesToggleState;
    }

    if (this.audienceAction === 'delete') {
      return SegmentsManageAudiencesDelete;
    }

    return;
  }

  @action
  onManage(audience) {
    this.setProperties({
      audienceAction: 'manage',
      audienceId: audience.id,
    });
  }

  @action
  onStopManaging() {
    this._stopManaging();
  }

  @action
  onStopAdding() {
    this.createAudienceTask.cancelAll({ resetState: true });
    this.set('isAdding', false);
  }

  @action
  didClickAddAudience() {
    this.analytics.track('audience', 'sync_creation_started', { model: this.model });
  }
}
